/**
 * ScreenRecorder Component
 *
 * Provides screen recording functionality for case review sessions.
 * Uses native MediaRecorder API with canvas compositing for webcam PiP.
 *
 * Features:
 * - Captures current browser tab only (not arbitrary screens)
 * - Optional webcam picture-in-picture (composited into the recording)
 * - Optional microphone audio for narration
 * - HQ 1080p @ 10 Mbps recording
 * - Auto-saves with study ID in filename
 * - Handles cleanup on unmount
 */

import React, { useState, useRef, useCallback, useEffect } from "react";
import ReactDOM from "react-dom";
import {
  FaVideo,
  FaStop,
  FaDownload,
  FaMicrophone,
  FaMicrophoneSlash,
  FaCamera,
  FaVideoSlash,
} from "../utils/icons";

interface ScreenRecorderProps {
  /** Study ID to include in the recording filename */
  studyId: string;
  /** Optional className for positioning */
  className?: string;
  /** Compact mode for sidebar placement */
  compact?: boolean;
}

type RecordingState = "idle" | "recording" | "stopped";

// Tiny floating stop button during recording
const FloatingRecordingControls: React.FC<{
  onStop: () => void;
}> = ({ onStop }) => {
  return ReactDOM.createPortal(
    <div className="fixed bottom-2 left-2 z-99999">
      <div
        className="flex items-center justify-center w-6 h-6 bg-signal text-white rounded-full shadow cursor-pointer hover:bg-signal/90 transition-colors"
        onClick={onStop}
        title="Stop recording"
      >
        <FaStop className="w-2 h-2" />
      </div>
    </div>,
    document.body,
  );
};

export const ScreenRecorder: React.FC<ScreenRecorderProps> = ({
  studyId,
  className = "",
  compact = false,
}) => {
  const [state, setState] = useState<RecordingState>("idle");
  const [includeAudio, setIncludeAudio] = useState(false);
  const [includeWebcam, setIncludeWebcam] = useState(false);
  const [showMediaPrompt, setShowMediaPrompt] = useState(false);
  const [, setRecordingDuration] = useState(0);
  const [blob, setBlob] = useState<Blob | null>(null);
  const [error, setError] = useState<string | null>(null);

  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const chunksRef = useRef<Blob[]>([]);
  const streamsRef = useRef<MediaStream[]>([]);
  const timerRef = useRef<number | null>(null);
  const animationFrameRef = useRef<number | null>(null);
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const webcamStreamRef = useRef<MediaStream | null>(null);

  // Cleanup function
  const cleanup = useCallback(() => {
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }

    if (animationFrameRef.current) {
      cancelAnimationFrame(animationFrameRef.current);
      animationFrameRef.current = null;
    }

    streamsRef.current.forEach((stream) => {
      stream.getTracks().forEach((track) => track.stop());
    });
    streamsRef.current = [];

    if (webcamStreamRef.current) {
      webcamStreamRef.current.getTracks().forEach((track) => track.stop());
      webcamStreamRef.current = null;
    }

    mediaRecorderRef.current = null;
    chunksRef.current = [];
    canvasRef.current = null;
  }, []);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (state === "recording") {
        cleanup();
      }
    };
  }, [state, cleanup]);

  const startRecording = useCallback(async () => {
    setShowMediaPrompt(false);
    setError(null);
    chunksRef.current = [];

    try {
      // Request screen capture - prefer current tab only
      const displayMediaOptions: DisplayMediaStreamOptions & {
        preferCurrentTab?: boolean;
        selfBrowserSurface?: "include" | "exclude";
        surfaceSwitching?: "include" | "exclude";
        monitorTypeSurfaces?: "include" | "exclude";
      } = {
        video: {
          frameRate: 30,
          width: { ideal: 1920 },
          height: { ideal: 1080 },
        },
        audio: false,
        preferCurrentTab: true,
        selfBrowserSurface: "include",
        surfaceSwitching: "exclude",
        monitorTypeSurfaces: "exclude",
      };

      const screenStream = await navigator.mediaDevices.getDisplayMedia(
        displayMediaOptions as DisplayMediaStreamOptions,
      );
      streamsRef.current.push(screenStream);

      // Get webcam stream if enabled
      let webcamStream: MediaStream | null = null;
      if (includeWebcam) {
        try {
          webcamStream = await navigator.mediaDevices.getUserMedia({
            video: {
              width: { ideal: 480 },
              height: { ideal: 360 },
              frameRate: 30,
            },
            audio: false,
          });
          webcamStreamRef.current = webcamStream;
          streamsRef.current.push(webcamStream);
        } catch (webcamErr) {
          console.warn("Webcam not available:", webcamErr);
        }
      }

      // Get microphone audio if enabled
      let audioStream: MediaStream | null = null;
      if (includeAudio) {
        try {
          audioStream = await navigator.mediaDevices.getUserMedia({
            audio: {
              echoCancellation: true,
              noiseSuppression: true,
            },
            video: false,
          });
          streamsRef.current.push(audioStream);
        } catch (audioErr) {
          console.warn("Microphone not available:", audioErr);
        }
      }

      // Get screen video track settings
      const screenTrack = screenStream.getVideoTracks()[0];
      const screenSettings = screenTrack.getSettings();
      const width = screenSettings.width || 1920;
      const height = screenSettings.height || 1080;

      // Create canvas for compositing
      const canvas = document.createElement("canvas");
      canvas.width = width;
      canvas.height = height;
      const ctx = canvas.getContext("2d")!;
      canvasRef.current = canvas;

      // Create video elements for drawing
      const screenVideo = document.createElement("video");
      screenVideo.srcObject = screenStream;
      screenVideo.muted = true;
      screenVideo.playsInline = true;
      await screenVideo.play();

      let webcamVideo: HTMLVideoElement | null = null;
      if (webcamStream) {
        webcamVideo = document.createElement("video");
        webcamVideo.srcObject = webcamStream;
        webcamVideo.muted = true;
        webcamVideo.playsInline = true;
        // Ensure video keeps playing even when tab is not focused
        webcamVideo.setAttribute("playsinline", "");
        await webcamVideo.play();
      }

      // PiP dimensions and position (bottom-right corner) - square shape
      const pipSize = Math.round(width * 0.12); // 12% of screen width, square
      const pipMargin = 20;
      const pipX = width - pipSize - pipMargin;
      const pipY = height - pipSize - pipMargin;

      // Animation loop to composite frames
      // Use a fixed interval instead of requestAnimationFrame for more consistent timing
      let lastFrameTime = 0;
      const targetFps = 30;
      const frameInterval = 1000 / targetFps;

      const drawFrame = (timestamp: number) => {
        // Throttle to target FPS for consistent timing
        if (timestamp - lastFrameTime < frameInterval) {
          animationFrameRef.current = requestAnimationFrame(drawFrame);
          return;
        }
        lastFrameTime = timestamp;

        // Draw screen capture
        ctx.drawImage(screenVideo, 0, 0, width, height);

        // Draw webcam PiP if available (circular, center-cropped)
        if (webcamVideo && webcamStream && webcamVideo.readyState >= 2) {
          ctx.save();

          // Create circular clip path
          ctx.beginPath();
          ctx.arc(pipX + pipSize / 2, pipY + pipSize / 2, pipSize / 2, 0, Math.PI * 2);
          ctx.clip();

          // Calculate center crop from webcam (crop to square from center)
          const vidW = webcamVideo.videoWidth || 480;
          const vidH = webcamVideo.videoHeight || 360;
          const cropSize = Math.min(vidW, vidH); // Square crop
          const srcX = (vidW - cropSize) / 2; // Center horizontally
          const srcY = (vidH - cropSize) / 2; // Center vertically

          // Draw webcam (mirrored for natural selfie view)
          ctx.translate(pipX + pipSize, pipY);
          ctx.scale(-1, 1);
          ctx.drawImage(webcamVideo, srcX, srcY, cropSize, cropSize, 0, 0, pipSize, pipSize);

          ctx.restore();

          // Draw circular border around PiP
          ctx.strokeStyle = "rgba(255, 255, 255, 0.9)";
          ctx.lineWidth = 3;
          ctx.beginPath();
          ctx.arc(pipX + pipSize / 2, pipY + pipSize / 2, pipSize / 2, 0, Math.PI * 2);
          ctx.stroke();
        }

        animationFrameRef.current = requestAnimationFrame(drawFrame);
      };
      animationFrameRef.current = requestAnimationFrame(drawFrame);

      // Capture canvas as stream
      const canvasStream = canvas.captureStream(30);

      // Add audio track if available
      if (audioStream) {
        audioStream.getAudioTracks().forEach((track) => {
          canvasStream.addTrack(track);
        });
      }

      // Create MediaRecorder
      const mimeType = MediaRecorder.isTypeSupported("video/webm;codecs=vp9")
        ? "video/webm;codecs=vp9"
        : "video/webm";

      const mediaRecorder = new MediaRecorder(canvasStream, {
        mimeType,
        videoBitsPerSecond: 16000000, // 16 Mbps for higher quality
      });

      mediaRecorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          chunksRef.current.push(event.data);
        }
      };

      mediaRecorder.onstop = () => {
        const recordedBlob = new Blob(chunksRef.current, { type: mimeType });
        setBlob(recordedBlob);
        setState("stopped");
        cleanup();
      };

      mediaRecorderRef.current = mediaRecorder;

      // Handle user stopping screen share via browser UI
      screenTrack.onended = () => {
        stopRecording();
      };

      // Start recording
      mediaRecorder.start(1000);
      setState("recording");
      setRecordingDuration(0);

      // Start duration timer
      timerRef.current = window.setInterval(() => {
        setRecordingDuration((d) => d + 1);
      }, 1000);
    } catch (err) {
      console.error("Failed to start recording:", err);
      if ((err as Error).name === "NotAllowedError") {
        setError("Recording cancelled");
      } else {
        setError("Failed to start recording");
      }
      cleanup();
    }
  }, [includeAudio, includeWebcam, cleanup]);

  const handleRecordClick = useCallback(() => {
    if (!includeAudio && !includeWebcam) {
      setShowMediaPrompt(true);
      return;
    }
    startRecording();
  }, [includeAudio, includeWebcam, startRecording]);

  const stopRecording = useCallback(() => {
    if (animationFrameRef.current) {
      cancelAnimationFrame(animationFrameRef.current);
      animationFrameRef.current = null;
    }

    if (mediaRecorderRef.current && mediaRecorderRef.current.state !== "inactive") {
      mediaRecorderRef.current.stop();
    }

    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
  }, []);

  const downloadRecording = useCallback(() => {
    if (!blob) return;

    const timestamp = new Date().toISOString().slice(0, 19).replace(/[:.]/g, "-");
    const sanitizedStudyId = studyId.replace(/[^a-zA-Z0-9-_]/g, "_").slice(0, 30);
    const filename = `case-${sanitizedStudyId}-${timestamp}.webm`;

    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }, [blob, studyId]);

  const resetRecording = useCallback(() => {
    setBlob(null);
    setState("idle");
    setRecordingDuration(0);
    setError(null);
  }, []);

  // During recording, show minimal floating indicator
  if (state === "recording") {
    return <FloatingRecordingControls onStop={stopRecording} />;
  }

  // Compact mode for sidebar
  if (compact) {
    return (
      <div className={`flex flex-col gap-2 ${className}`}>
        {state === "idle" && (
          <>
            <button
              onClick={handleRecordClick}
              className="sidebar-btn w-full px-2 py-2 text-[10px] font-medium text-fg-muted hover:text-fg gap-1.5 justify-center"
              title="Record session (HQ 1080p)"
            >
              <FaVideo className="w-3 h-3" />
              <span>Record</span>
            </button>
            {showMediaPrompt && (
              <div className="flex flex-col gap-1.5 p-2 bg-surface-2 rounded text-[9px]">
                <span className="text-fg-muted text-center">Enable before recording:</span>
                <div className="flex items-center justify-center gap-3">
                  <button
                    onClick={() => {
                      setIncludeAudio(!includeAudio);
                    }}
                    className={`flex items-center gap-1 transition-colors ${
                      includeAudio ? "text-signal-text-tint" : "text-fg-muted hover:text-fg"
                    }`}
                  >
                    {includeAudio ? (
                      <FaMicrophone className="w-2.5 h-2.5" />
                    ) : (
                      <FaMicrophoneSlash className="w-2.5 h-2.5" />
                    )}
                    <span>Mic</span>
                  </button>
                  <button
                    onClick={() => {
                      setIncludeWebcam(!includeWebcam);
                    }}
                    className={`flex items-center gap-1 transition-colors ${
                      includeWebcam ? "text-signal-text-tint" : "text-fg-muted hover:text-fg"
                    }`}
                  >
                    {includeWebcam ? (
                      <FaCamera className="w-2.5 h-2.5" />
                    ) : (
                      <FaVideoSlash className="w-2.5 h-2.5" />
                    )}
                    <span>Cam</span>
                  </button>
                </div>
                <button
                  onClick={startRecording}
                  className="mt-1 px-2 py-1 bg-signal hover:bg-signal/90 text-white rounded text-[9px] transition-colors"
                >
                  Record anyway
                </button>
              </div>
            )}
            {!showMediaPrompt && (
              <div className="flex items-center justify-center gap-3 text-[9px]">
                <button
                  onClick={() => setIncludeAudio(!includeAudio)}
                  className={`flex items-center gap-1 transition-colors ${
                    includeAudio ? "text-signal-text-tint" : "text-fg-muted hover:text-fg"
                  }`}
                  title={includeAudio ? "Mic on" : "Mic off"}
                >
                  {includeAudio ? (
                    <FaMicrophone className="w-2.5 h-2.5" />
                  ) : (
                    <FaMicrophoneSlash className="w-2.5 h-2.5" />
                  )}
                  <span>Mic</span>
                </button>
                <button
                  onClick={() => setIncludeWebcam(!includeWebcam)}
                  className={`flex items-center gap-1 transition-colors ${
                    includeWebcam ? "text-signal-text-tint" : "text-fg-muted hover:text-fg"
                  }`}
                  title={includeWebcam ? "Webcam on" : "Webcam off"}
                >
                  {includeWebcam ? (
                    <FaCamera className="w-2.5 h-2.5" />
                  ) : (
                    <FaVideoSlash className="w-2.5 h-2.5" />
                  )}
                  <span>Cam</span>
                </button>
              </div>
            )}
            {error && <span className="text-[9px] text-warn text-center">{error}</span>}
          </>
        )}

        {state === "stopped" && blob && (
          <>
            <button
              onClick={downloadRecording}
              className="sidebar-btn w-full px-2 py-2 text-[10px] font-medium text-signal-text-tint hover:text-fg gap-1.5 justify-center bg-signal-tint"
            >
              <FaDownload className="w-3 h-3" />
              <span>{(blob.size / 1024 / 1024).toFixed(1)} MB</span>
            </button>
            <button
              onClick={resetRecording}
              className="text-[9px] text-fg-muted hover:text-fg transition-colors text-center"
            >
              New recording
            </button>
          </>
        )}
      </div>
    );
  }

  // Default horizontal layout
  return (
    <div className={`flex items-center gap-2 ${className}`}>
      {state === "idle" && (
        <>
          {/* Webcam toggle */}
          <button
            onClick={() => setIncludeWebcam(!includeWebcam)}
            className={`p-2 rounded-md transition-colors ${
              includeWebcam ? "bg-signal text-white" : "bg-surface-2 text-fg-muted hover:text-fg"
            }`}
            title={includeWebcam ? "Webcam enabled (PiP)" : "Enable webcam PiP"}
          >
            {includeWebcam ? (
              <FaCamera className="w-4 h-4" />
            ) : (
              <FaVideoSlash className="w-4 h-4" />
            )}
          </button>

          {/* Audio toggle */}
          <button
            onClick={() => setIncludeAudio(!includeAudio)}
            className={`p-2 rounded-md transition-colors ${
              includeAudio ? "bg-signal text-white" : "bg-surface-2 text-fg-muted hover:text-fg"
            }`}
            title={includeAudio ? "Microphone enabled" : "Microphone disabled"}
          >
            {includeAudio ? (
              <FaMicrophone className="w-4 h-4" />
            ) : (
              <FaMicrophoneSlash className="w-4 h-4" />
            )}
          </button>

          {/* Start button */}
          <button
            onClick={handleRecordClick}
            className="flex items-center gap-2 px-4 py-2 bg-signal hover:bg-signal/90 text-white rounded-md text-sm font-medium transition-colors shadow-lg"
          >
            <FaVideo className="w-4 h-4" />
            <span>Record Session</span>
          </button>

          {error && <span className="text-xs text-warn">{error}</span>}
        </>
      )}

      {state === "stopped" && blob && (
        <>
          {/* Download button */}
          <button
            onClick={downloadRecording}
            className="flex items-center gap-2 px-4 py-2 bg-signal hover:bg-signal/90 text-white rounded-md text-sm font-medium transition-colors shadow-lg"
          >
            <FaDownload className="w-4 h-4" />
            <span>Download ({(blob.size / 1024 / 1024).toFixed(1)} MB)</span>
          </button>

          {/* New recording button */}
          <button
            onClick={resetRecording}
            className="px-4 py-2 bg-surface hover:bg-surface-2 text-fg rounded-md text-sm transition-colors"
          >
            New Recording
          </button>
        </>
      )}
    </div>
  );
};

export default ScreenRecorder;
